import { useAppSelector } from '../store/hooks';

// Define account types that are considered child accounts
const CHILD_ACCOUNT_TYPES = ['child', 'junior', 'kids', 'minor'];

// Define restricted pages for child accounts
const RESTRICTED_PAGES_FOR_CHILDREN = [
  '/cards',
  '/crypto',
  '/assets',
  '/settings'
];

// Define allowed pages for child accounts
const ALLOWED_PAGES_FOR_CHILDREN = [
  '/',
  '/dashboard',
  '/account',
  '/transactions',
  '/payments',
  '/savings',
  '/child-wallets',
  '/mt5-trading',
  '/trading-bots',
  '/social-media'
];

export interface AccessControlResult {
  hasAccess: boolean;
  isChildAccount: boolean;
  redirectPath?: string;
}

export const useAccessControl = () => {
  const { accounts } = useAppSelector((state) => state.accounts);
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const { profile } = useAppSelector((state) => state.profile);
  const authPermissions = useAppSelector((state: any) => state?.auth?.permissions || [], (left, right) => {
    // Custom equality check to prevent unnecessary rerenders
    if (left.length !== right.length) return false;
    return left.every((permission: string, index: number) => permission === right[index]);
  });

  // Check if user has any child account types - now using backend profile data
  const isChildAccount = () => {
    // First check if we have profile data from backend with the new fields
    if (profile?.accountType) {
      // Handle both possible field names from backend
      const isParent = profile.isParentAccount !== undefined 
        ? profile.isParentAccount 
        : (profile as any).parentAccount; // Handle the actual backend response
      
      return profile.accountType === 'CHILD' || !isParent;
    }
    
    // Fallback to old method if profile not loaded yet
    if (!accounts || accounts.length === 0) return false;
    
    return accounts.some(account => 
      CHILD_ACCOUNT_TYPES.includes(account.accountType.toLowerCase())
    );
  };

  // Check if a specific page is accessible
  const checkPageAccess = (path: string): AccessControlResult => {
    if (!isAuthenticated) {
      return {
        hasAccess: false,
        isChildAccount: false,
        redirectPath: '/login'
      };
    }

    const isChild = isChildAccount();

    // Permission mapping for retail routes
    const permissionRequiredForPath: Record<string, string | undefined> = {
      '/dashboard': 'VIEW_RETAIL_DASHBOARD',
      '/account': 'VIEW_RETAIL_ACCOUNTS',
      '/transactions': 'VIEW_RETAIL_TRANSACTIONS',
      '/payments': 'VIEW_RETAIL_PAYMENTS',
      '/payees': 'VIEW_RETAIL_PAYMENTS',
      '/cards': 'VIEW_RETAIL_CARDS',
      '/savings': 'VIEW_RETAIL_SAVINGS',
      '/crypto': 'VIEW_RETAIL_CRYPTO',
      '/assets': 'VIEW_RETAIL_ASSETS',
      '/mt5-trading': 'VIEW_RETAIL_MT5_TRADING',
      '/trading-bots': 'VIEW_RETAIL_TRADING_BOTS',
      '/crypto-portfolio': 'VIEW_RETAIL_CRYPTO_PORTFOLIO',
      '/order-book': 'VIEW_RETAIL_ORDER_BOOK',
      '/igaming': 'VIEW_RETAIL_IGAMING',
      '/p2p-games': 'VIEW_RETAIL_P2PGAME',
      '/settings': 'VIEW_RETAIL_SETTINGS',
      '/profile': 'VIEW_RETAIL_PROFILE',
      '/child-wallets': 'VIEW_RETAIL_CHILD_WALLETS',
      '/social-media': 'VIEW_RETAIL_SOCIAL_MEDIA',
      '/creator-chat': 'VIEW_RETAIL_CREATOR_CHAT',
      '/documents': 'VIEW_RETAIL_DOCUMENTS',
      '/notifications': 'VIEW_RETAIL_NOTIFICATIONS',
      '/support': 'VIEW_RETAIL_SUPPORT',
      '/alerts': 'VIEW_RETAIL_ALERTS'
    };
    const requiredKey = Object.keys(permissionRequiredForPath).find(key => path === key || path.startsWith(key + '/'));
    const requiredPermission = requiredKey ? permissionRequiredForPath[requiredKey] : undefined;
    const userPermissions: string[] = (profile as any)?.permissions || authPermissions || [];

    if (isChild) {
      // For child accounts, only allow specific pages
      const hasAccess = ALLOWED_PAGES_FOR_CHILDREN.includes(path) || 
                       path.startsWith('/savings/') || 
                       path.startsWith('/transactions/');
      const permissionAllowed = !requiredPermission || userPermissions.includes(requiredPermission);
      return {
        hasAccess,
        isChildAccount: true,
        redirectPath: hasAccess && permissionAllowed ? undefined : '/dashboard'
      };
    }

    // All users: enforce permission if defined
    const permissionAllowed = !requiredPermission || userPermissions.includes(requiredPermission);
    return {
      hasAccess: permissionAllowed,
      isChildAccount: false,
      redirectPath: permissionAllowed ? undefined : '/dashboard'
    };
  };

  // Get filtered navigation items based on account type
  const getFilteredNavigation = () => {
    const isChild = isChildAccount();
    const allNavItems = [
      { path: '/dashboard', label: 'Dashboard', icon: '🏠', perm: 'VIEW_RETAIL_DASHBOARD' },
      { path: '/account', label: 'Account', icon: '👤', perm: 'VIEW_RETAIL_ACCOUNTS' },
      { path: '/transactions', label: 'Transactions', icon: '📊', perm: 'VIEW_RETAIL_TRANSACTIONS' },
      { path: '/payments', label: 'Payments', icon: '💸', perm: 'VIEW_RETAIL_PAYMENTS' },
      { path: '/payees', label: 'Payees', icon: '👥', perm: 'VIEW_RETAIL_PAYMENTS' },
      { path: '/child-wallets', label: 'Child Wallets', icon: '👶', perm: 'VIEW_RETAIL_CHILD_WALLETS' },
      { path: '/cards', label: 'Cards', icon: '💳', perm: 'VIEW_RETAIL_CARDS' },
      { path: '/savings', label: 'Savings', icon: '🐷', perm: 'VIEW_RETAIL_SAVINGS' },
      { path: '/crypto', label: 'Crypto', icon: '🪙', perm: 'VIEW_RETAIL_CRYPTO' },
      { path: '/assets', label: 'Assets', icon: '📈', perm: 'VIEW_RETAIL_ASSETS' },
      { path: '/mt5-trading', label: 'MT5 Trading', icon: '📈', perm: 'VIEW_RETAIL_MT5_TRADING' },
      { path: '/trading-bots', label: 'Trading Bots', icon: '🤖', perm: 'VIEW_RETAIL_TRADING_BOTS' },
      { path: '/crypto-portfolio', label: 'Crypto Portfolio', icon: '💼', perm: 'VIEW_RETAIL_CRYPTO_PORTFOLIO' },
      { path: '/order-book', label: 'Order Book', icon: '📖', perm: 'VIEW_RETAIL_ORDER_BOOK' },
      { path: '/market', label: 'Market signals', icon: '📡', perm: 'VIEW_MARKET_SIGNALS' },
      { path: '/igaming', label: 'iGaming', icon: '🎰', perm: 'VIEW_RETAIL_IGAMING' },
      { path: '/p2p-games', label: 'P2P Games', icon: '🎮', perm: 'VIEW_RETAIL_P2PGAME' },
      { path: '/creator-chat', label: 'AI Creator Chat', icon: '💬', perm: 'VIEW_RETAIL_CREATOR_CHAT' },
      { path: '/social-media', label: 'Social Media', icon: '📱', perm: 'VIEW_RETAIL_SOCIAL_MEDIA' },
      { path: '/settings', label: 'Settings', icon: '⚙️', perm: 'VIEW_RETAIL_SETTINGS' }
    ] as Array<{ path: string; label: string; icon: string; perm?: string }>;

    const userPermissions: string[] = (profile as any)?.permissions || authPermissions || [];
    
    if (isChild) {
      return allNavItems
        .filter(item => ALLOWED_PAGES_FOR_CHILDREN.includes(item.path))
        .filter(item => !item.perm || userPermissions.includes(item.perm));
    }

    // All users (retail and admin): filter by permissions if perm specified
    return allNavItems.filter(item => !item.perm || userPermissions.includes(item.perm));
  };

  return {
    isChildAccount: isChildAccount(),
    checkPageAccess,
    getFilteredNavigation,
    restrictedPages: RESTRICTED_PAGES_FOR_CHILDREN,
    allowedPages: ALLOWED_PAGES_FOR_CHILDREN,
    // Expose profile data for debugging
    profileAccountType: profile?.accountType,
    profileIsParentAccount: profile?.isParentAccount,
    profileParentAccount: (profile as any)?.parentAccount,
    debugInfo: {
      hasProfile: !!profile,
      accountType: profile?.accountType,
      isParentAccount: profile?.isParentAccount,
      parentAccount: (profile as any)?.parentAccount,
      calculatedIsChild: isChildAccount()
    }
  };
};

/** Admin: open/close futures from Market Signals drawer (without full MANAGE_TRADING_BOTS). */
export function adminCanOpenMarketSignalPositions(
  adminUser?: { role?: string; permissions?: string[] } | null
): boolean {
  if (!adminUser) return false;
  if (adminUser.role === 'SUPER_ADMIN') return true;
  const perms = adminUser.permissions ?? [];
  return perms.includes('MANAGE_TRADING_BOTS') || perms.includes('OPEN_MARKET_SIGNAL_POSITIONS');
}

export default useAccessControl; 